import { Attachment } from '@digital-platform/attachments/domain';
import {
  DisposalSite,
  DisposalSiteModel,
} from '@digital-platform/disposal-sites/domain';
import { DocusignData } from '@digital-platform/docusign/domain';
import { Generator, GeneratorModel } from '@digital-platform/generators/domain';
import {
  DomainModelHelper,
  EntityBase,
  Contact,
  ContactModel,
} from '@digital-platform/shared/domain';
import {
  Signature,
  SignatureModel,
  SignNowDocument,
  SignNowDocumentModel,
} from '@digital-platform/sign-now/domain';
import { User, UserModel } from '@digital-platform/users/domain';
import { InternalStatus } from './../enums/internal-status';
import { WasteProfileStatus } from '../enums/waste-profile-status';
import {
  WasteProfileMeta,
  WasteProfile,
  RecertificationDetails,
  ChangeRequestDetails,
  AdditionalSupplement,
} from '../interfaces';
import { CheckList } from '../interfaces/checklist.interface';
import { InternalStatusHistory } from '../interfaces/internal-status-log.interface';
import { ThirdPartyAuthDetails } from '../interfaces/waste-profile-3pa.interface';
import { CommentLogHistory } from '../interfaces/waste-profile-comment-log.interface';
import { DynamoSafeWasteProfile } from '../interfaces/waste-profile.interface';
import { ChangeRequestDetailsModel } from './change-request-details.model';
import { CheckListModel } from './checkList.model';
import { InternalStatusHistoryModel } from './internal-status-log.model';
import { RecertificationDetailsModel } from './recertification-details.model';
import { ThirdPartyAuthModel } from './waste-profile-3pa.model';
import { CommentLogHistoryModel } from './waste-profile-comment-log.model';

export class WasteProfileModel extends EntityBase implements WasteProfile {
  static readonly DEFAULT_CONFIG: WasteProfile = {
    meta: {
      sections: {
        generator: {
          completed: false,
          valid: false,
        },
        billing: {
          completed: false,
          valid: false,
        },
        wasteStream: {
          completed: false,
          valid: false,
        },
        attachments: {
          completed: false,
          valid: false,
        },
        characteristics: {
          completed: false,
          valid: false,
        },
        destination: {
          completed: false,
          valid: false,
        },
        rcra: {
          completed: false,
          valid: false,
        },
        checkList: {
          completed: false,
          valid: false,
        },
        signature: {
          completed: false,
          valid: false,
        },
      },
    },
    schemaVersion: 1,
    type: null,
    active: true,
    version: 1,
    createdBy: null,
    createdDate: null,
    lastModifiedBy: null,
    lastModifiedDate: null,
    submittedDate: null,
    certifiedDate: null,
    expectedResponseDate: null,
    approvedDate: null,
    approverName: '',
    incompleteBy: '',
    rejectedBy: '',
    rejectedDate: null,
    incompleteDate: null,
    expiryDate: null,
    id: null,
    isLegacyProfile: false,
    isDuplicate: false,
    duplicatedFromId: null,
    name: '',
    customerName: null,
    number: '',
    status: WasteProfileStatus.NOT_SUBMITTED,
    internalStatus: InternalStatus.PENDING_CUSTOMER_INFO,
    landfill: '',
    wasteType: null,
    frequency: null,
    processGeneratingWaste: '',
    generator: null,
    destination: null,
    checkList: null,
    approvalNotes: '',
    analyticalRequiredOnNextRecert: false,
    recertAnalyticalCustomerNote: '',
    stateApprovalWasteCodeNotApplicable: false,
    incompleteNotes: '',
    rejectionNotes: '',
    salesRepName: '',
    salesRepNumber: '',
    creator: {
      id: null,
      name: '',
      email: null,
      phone: '',
      phoneExt: null,
      fax: null,
    },
    offlineExists: null,
    offlineApproved: null,

    pendingRecertification: null,
    recertificationArchive: [],
    pendingChangeRequest: null,
    changeRequestArchive: [],
    yearCode: null,
    logbookSequenceNumber: null,
    logbookId: null,
    logbookNotes: '',
    truxMaterialDescription: '',
    isRecertification: false,
    isChange: false,
    isRecertWithChange: false,
    isExpired: false,
    certificationComments: '',
    certificationName: '',
    certificationTitle: '',
    certificationEmail: '',
    certificationSignature: false,
    additionalComments: '',
    regulatoryIdNumberNotApplicable: false,
    signNowDocUrl: null,
    signNowDocument: null,
    signatures: [],
    signedDocument: null,
    docusignData: null,
    log: [],
    commentLog: [],
    coordinator: null,
    thirdPartyAuth: null,
    thirdPartyAuthAttachment: null,
    additionalAdminSupplements: [],
    thirdPartyAuthAdminAttachment: null,
    stateInternalAdminAttachments: [],
    internalAdminAttachments: [],
  };

  // meta info
  readonly schemaVersion: 1;
  readonly type: string;
  readonly active: boolean;
  readonly version: number;
  readonly meta: WasteProfileMeta;
  readonly createdBy: string | null;
  readonly createdDate: Date;
  readonly lastModifiedBy: string | null;
  readonly lastModifiedDate: Date;
  readonly creator: Contact;
  readonly customerName: string;
  readonly status: WasteProfileStatus;
  readonly internalStatus: InternalStatus;
  readonly isLegacyProfile: boolean;
  readonly isDuplicate: boolean;
  readonly duplicatedFromId: string;
  readonly holdNotifications?: boolean;
  readonly pendingNotifications?: boolean;

  // generator & billing steps
  readonly generator: Generator;

  // waste info step
  readonly name: string;
  readonly wasteType: string;
  readonly processGeneratingWaste: string;

  // waste info or physical characteristics step
  readonly frequency: string;

  // destination step
  readonly landfill: string;
  readonly destination: DisposalSite;

  // checklist step
  readonly checkList: CheckList;

  // signature step
  readonly signNowDocUrl: string;
  readonly signNowDocument: SignNowDocument;
  readonly signatures: Signature[];
  readonly signedDocument: Attachment;
  readonly docusignData: DocusignData;
  readonly;
  // TODO: investigate these fields? From legacy profiles?
  readonly certificationComments: string;
  readonly certificationName: string;
  readonly certificationTitle: string;
  readonly certificationEmail: string;
  readonly certificationSignature: boolean;

  // change & recert data
  readonly pendingRecertification: RecertificationDetails;
  readonly recertificationArchive: RecertificationDetails[];
  readonly pendingChangeRequest: ChangeRequestDetails;
  readonly changeRequestArchive: ChangeRequestDetails[];

  // logbook data
  readonly number: string;
  readonly log: InternalStatusHistory[];
  readonly commentLog: CommentLogHistory[];
  readonly yearCode: string;
  readonly logbookSequenceNumber: string;
  readonly logbookId: string;
  readonly logbookNotes: string;
  readonly truxMaterialDescription: string;
  readonly approvalNotes: string;
  readonly analyticalRequiredOnNextRecert: boolean;
  readonly incompleteNotes: string;
  readonly rejectionNotes: string;
  readonly salesRepName: string;
  readonly salesRepNumber: string;
  readonly coordinator: User;
  readonly recertAnalyticalCustomerNote: string;

  // status and dates
  readonly approverName: string;
  readonly incompleteBy: string;
  readonly rejectedBy: string;
  readonly submittedDate: Date;
  readonly certifiedDate: Date;
  readonly expectedResponseDate: Date;
  readonly approvedDate: Date;
  readonly rejectedDate: Date;
  readonly incompleteDate: Date;
  readonly expiryDate: Date;

  // legacy flags?
  readonly isRecertification: boolean;
  readonly isRecertWithChange: boolean;
  readonly isChange: boolean;
  readonly isExpired: boolean;

  // 3pa
  readonly thirdPartyAuth: ThirdPartyAuthDetails;
  readonly thirdPartyAuthAttachment: Attachment;

  //admin attachments
  readonly additionalAdminSupplements: AdditionalSupplement[];
  readonly thirdPartyAuthAdminAttachment: Attachment;
  readonly stateInternalAdminAttachments: Attachment[];
  readonly internalAdminAttachments: Attachment[];

  readonly stateApprovalWasteCodeNotApplicable: boolean | null;

  // offline steps
  readonly offlineExists: boolean;
  readonly offlineApproved: boolean;

  // unused?
  readonly additionalComments: string;
  readonly regulatoryIdNumberNotApplicable: boolean;

  constructor(config?: WasteProfile | DynamoSafeWasteProfile) {
    super(config);
    this.schemaVersion = config ? config.schemaVersion : 1;
    this.version = config ? config.version : 1;
    this.active = config ? config.active : true;
    this.isLegacyProfile = config ? config.isLegacyProfile : false;
    this.isDuplicate = config ? config.isDuplicate : false;
    this.isRecertification = config ? config.isRecertification : false;
    this.isChange = config ? config.isChange : false;
    this.isRecertWithChange = config ? config.isRecertWithChange : false;
    this.isExpired = config ? config.isExpired : false;
    this.analyticalRequiredOnNextRecert = config
      ? config.analyticalRequiredOnNextRecert || false
      : false;
    this.recertAnalyticalCustomerNote = config
      ? DomainModelHelper.ensureDataIsString(
          config.recertAnalyticalCustomerNote
        )
      : null;
    this.regulatoryIdNumberNotApplicable = config
      ? config.regulatoryIdNumberNotApplicable
      : false;
    this.duplicatedFromId = config
      ? DomainModelHelper.ensureDataIsString(config.duplicatedFromId)
      : null;
    this.holdNotifications = config
      ? DomainModelHelper.ensureDataIsBoolean(config.holdNotifications)
      : false;
    this.pendingNotifications = config
      ? DomainModelHelper.ensureDataIsBoolean(config.pendingNotifications)
      : false;
    this.meta = config
      ? this.createMeta(config.meta)
      : WasteProfileModel.DEFAULT_CONFIG.meta;
    this.customerName = config
      ? DomainModelHelper.ensureDataIsString(config.customerName)
      : null;
    this.approvedDate = config
      ? DomainModelHelper.ensureDataIsDate(config.approvedDate)
      : null;
    this.expiryDate = config
      ? config.expiryDate
        ? DomainModelHelper.ensureDataIsDate(config.expiryDate)
        : null
      : null;
    this.submittedDate = config
      ? config.submittedDate
        ? DomainModelHelper.ensureDataIsDate(config.submittedDate)
        : null
      : null;
    this.certifiedDate = config
      ? config.certifiedDate
        ? DomainModelHelper.ensureDataIsDate(config.certifiedDate)
        : null
      : null;
    this.expectedResponseDate = config
      ? config.expectedResponseDate
        ? DomainModelHelper.ensureDataIsDate(config.expectedResponseDate)
        : null
      : null;
    this.rejectedDate = config
      ? config.rejectedDate
        ? DomainModelHelper.ensureDataIsDate(config.rejectedDate)
        : null
      : null;
    this.incompleteDate = config
      ? config.incompleteDate
        ? DomainModelHelper.ensureDataIsDate(config.incompleteDate)
        : null
      : null;
    this.landfill = config
      ? DomainModelHelper.ensureDataIsString(config.landfill)
      : null;
    this.frequency = config
      ? DomainModelHelper.ensureDataIsString(config.frequency)
      : null;
    this.destination = config
      ? this.createDisposalSite(config.destination)
      : null;
    this.checkList = config ? this.createCheckList(config.checkList) : null;
    this.name = config
      ? DomainModelHelper.ensureDataIsString(config.name)
      : null;
    this.wasteType = config
      ? DomainModelHelper.ensureDataIsString(config.wasteType)
      : null;
    this.number = config
      ? DomainModelHelper.ensureDataIsString(config.number)
      : null;
    this.processGeneratingWaste = config
      ? DomainModelHelper.ensureDataIsString(config.processGeneratingWaste)
      : null;
    this.status =
      config && config.status
        ? config.status
        : WasteProfileStatus.NOT_SUBMITTED;
    this.internalStatus =
      config && config.internalStatus
        ? config.internalStatus
        : InternalStatus.PENDING_CUSTOMER_INFO;
    this.generator = config
      ? this.createGenerator(config.generator)
      : this.createGenerator(GeneratorModel.DEFAULT_CONFIG);
    this.creator = config
      ? {
          name: config.creator?.name || '',
          email: config.creator?.email || '',
          phone: config.creator?.phone || '',
          phoneExt: config.creator?.phoneExt || null,
          fax: config.creator?.fax || null,
        }
      : {
          name: '',
          email: '',
          phone: '',
          phoneExt: null,
          fax: null,
        };
    this.signatures = config ? this.createSignatures(config.signatures) : [];
    this.signedDocument = config ? config.signedDocument : null;
    this.signNowDocUrl = config ? config.signNowDocUrl : null;
    this.docusignData = config ? config.docusignData : null;
    this.signNowDocument = config
      ? this.createSignNowDocument(config.signNowDocument)
      : this.createSignNowDocument(SignNowDocumentModel.DEFAULT_CONFIG);
    this.certificationComments = config
      ? DomainModelHelper.ensureDataIsString(config.certificationComments)
      : null;
    this.certificationName = config
      ? DomainModelHelper.ensureDataIsString(config.certificationName)
      : null;
    this.certificationTitle = config
      ? DomainModelHelper.ensureDataIsString(config.certificationTitle)
      : null;
    this.certificationEmail = config
      ? DomainModelHelper.ensureDataIsString(config.certificationEmail)
      : null;
    this.certificationSignature = config
      ? DomainModelHelper.ensureDataIsBoolean(config.certificationSignature)
      : null;
    this.yearCode = config
      ? DomainModelHelper.ensureDataIsString(config.yearCode)
      : null;
    this.logbookId = config
      ? DomainModelHelper.ensureDataIsString(config.logbookId)
      : null;
    this.logbookSequenceNumber = config
      ? DomainModelHelper.ensureDataIsString(config.logbookSequenceNumber)
      : null;
    this.logbookNotes = config
      ? DomainModelHelper.ensureDataIsString(config.logbookNotes)
      : null;
    this.approverName = config
      ? DomainModelHelper.ensureDataIsString(config.approverName)
      : '';
    this.incompleteBy = config
      ? DomainModelHelper.ensureDataIsString(config.incompleteBy)
      : '';
    this.rejectedBy = config
      ? DomainModelHelper.ensureDataIsString(config.rejectedBy)
      : '';
    this.approvalNotes = config
      ? DomainModelHelper.ensureDataIsString(config.approvalNotes)
      : null;
    this.incompleteNotes = config
      ? DomainModelHelper.ensureDataIsString(config.incompleteNotes)
      : null;
    this.rejectionNotes = config
      ? DomainModelHelper.ensureDataIsString(config.rejectionNotes)
      : null;
    this.salesRepName = config
      ? DomainModelHelper.ensureDataIsString(config.salesRepName)
      : null;
    this.salesRepNumber = config
      ? DomainModelHelper.ensureDataIsString(config.salesRepNumber)
      : null;
    this.coordinator = config
      ? this.createCoordinator(config.coordinator)
      : null;
    this.truxMaterialDescription = config
      ? DomainModelHelper.ensureDataIsString(config.truxMaterialDescription)
      : null;
    this.additionalComments = config
      ? DomainModelHelper.ensureDataIsString(config.additionalComments)
      : null;
    this.pendingRecertification = config
      ? this.createPendingRecertification(config.pendingRecertification)
      : null;
    this.recertificationArchive = config
      ? this.createRecertificationArchive(config.recertificationArchive)
      : [];
    this.pendingChangeRequest = config
      ? this.createPendingChangeRequest(config.pendingChangeRequest)
      : null;
    this.changeRequestArchive = config
      ? this.createChangeRequestArchive(config.changeRequestArchive)
      : [];
    this.log = config ? this.createLog(config.log) : [];
    this.commentLog = config ? this.createCommentLog(config.commentLog) : [];
    this.thirdPartyAuthAttachment = config
      ? config.thirdPartyAuthAttachment
      : null;
    this.thirdPartyAuth = config
      ? this.createthirdPartyAuth(config.thirdPartyAuth)
      : this.createthirdPartyAuth(ThirdPartyAuthModel.DEFAULT_CONFIG);
    this.additionalAdminSupplements = config
      ? config.additionalAdminSupplements
      : [];
    this.thirdPartyAuthAdminAttachment = config
      ? config.thirdPartyAuthAdminAttachment
      : null;
    this.stateInternalAdminAttachments = config
      ? config.stateInternalAdminAttachments
      : [];
    this.internalAdminAttachments = config
      ? config.internalAdminAttachments
      : [];
    this.offlineExists = config ? config.offlineExists : null;
    this.offlineApproved = config ? config.offlineApproved : null;
  }

  static readonly needsConfirmation = (wasteProfile: WasteProfile): boolean => {
    // if it's not legacy, it flatly doesn't need it
    if (!wasteProfile.isLegacyProfile) {
      return false;
    }

    // legacy profiles that are version 1 definitely need to be reviewed
    if (wasteProfile.version === 1) {
      return true;
    }

    // legacy profiles that were auto expired will have version 2
    if (
      wasteProfile.status === WasteProfileStatus.EXPIRED &&
      wasteProfile.version === 2 &&
      wasteProfile.lastModifiedBy === 'System Automation (Cron)' // necessary?
    ) {
      return true;
    }

    return false;
  };

  // excludes the number field
  public getValue(): WasteProfile {
    return {
      id: this.id,
      schemaVersion: this.schemaVersion,
      type: this.type,
      active: this.active,
      version: this.version,
      meta: this.meta,
      createdBy: this.createdBy,
      createdDate: this.createdDate,
      lastModifiedBy: this.lastModifiedBy,
      lastModifiedDate: this.lastModifiedDate,
      creator: this.creator,
      customerName: this.customerName,
      status: this.status,
      internalStatus: this.internalStatus,
      isLegacyProfile: this.isLegacyProfile,
      isDuplicate: this.isDuplicate,
      duplicatedFromId: this.duplicatedFromId,
      landfill: this.landfill,
      wasteType: this.wasteType,
      frequency: this.frequency,
      processGeneratingWaste: this.processGeneratingWaste,
      generator: this.generator,
      destination: this.destination,
      checkList: this.checkList,
      name: this.name,
      number: this.number,
      offlineExists: this.offlineExists,
      offlineApproved: this.offlineApproved,
      signNowDocUrl: this.signNowDocUrl,
      signNowDocument: this.signNowDocument,
      signatures: this.signatures,
      signedDocument: this.signedDocument,
      docusignData: this.docusignData,
      certificationComments: this.certificationComments,
      certificationName: this.certificationName,
      certificationTitle: this.certificationTitle,
      certificationEmail: this.certificationEmail,
      certificationSignature: this.certificationSignature,
      yearCode: this.yearCode,
      logbookSequenceNumber: this.logbookSequenceNumber,
      logbookId: this.logbookId,
      logbookNotes: this.logbookNotes,
      truxMaterialDescription: this.truxMaterialDescription,
      approvalNotes: this.approvalNotes,
      analyticalRequiredOnNextRecert: this.analyticalRequiredOnNextRecert,
      incompleteNotes: this.incompleteNotes,
      rejectionNotes: this.rejectionNotes,
      salesRepName: this.salesRepName,
      salesRepNumber: this.salesRepNumber,
      coordinator: this.coordinator,
      recertAnalyticalCustomerNote: this.recertAnalyticalCustomerNote,
      approverName: this.approverName,
      incompleteBy: this.incompleteBy,
      rejectedBy: this.rejectedBy,
      submittedDate: this.submittedDate,
      certifiedDate: this.certifiedDate,
      expectedResponseDate: this.expectedResponseDate,
      approvedDate: this.approvedDate,
      rejectedDate: this.rejectedDate,
      incompleteDate: this.incompleteDate,
      expiryDate: this.expiryDate,
      isRecertification: this.isRecertification,
      isChange: this.isChange,
      isRecertWithChange: this.isRecertWithChange,
      isExpired: this.isExpired,
      pendingRecertification: this.pendingRecertification,
      recertificationArchive: this.recertificationArchive,
      pendingChangeRequest: this.pendingChangeRequest,
      changeRequestArchive: this.changeRequestArchive,
      log: this.log,
      commentLog: this.commentLog,
      thirdPartyAuth: this.thirdPartyAuth,
      thirdPartyAuthAttachment: this.thirdPartyAuthAttachment,
      additionalAdminSupplements: this.additionalAdminSupplements,
      internalAdminAttachments: this.internalAdminAttachments,
      thirdPartyAuthAdminAttachment: this.thirdPartyAuthAdminAttachment,
      stateInternalAdminAttachments: this.stateInternalAdminAttachments,
      stateApprovalWasteCodeNotApplicable:
        this.stateApprovalWasteCodeNotApplicable,
      additionalComments: this.additionalComments,
      regulatoryIdNumberNotApplicable: this.regulatoryIdNumberNotApplicable,
    };
  }
  // TODO: Clean this up when refactoring to use zod schemas
  // TODO: we can eliminate the parseWasteProfile logic and just do something like this right on the model
  public toDynamoSafeValue(): DynamoSafeWasteProfile {
    const wasteProfileValues = this.getValue();

    const safeValue = {
      ...wasteProfileValues,
    };

    if (!this.number || this.number.length > 0) {
      delete safeValue.number;
    }

    return safeValue;
  }

  protected createMeta(meta: WasteProfileMeta): WasteProfileMeta {
    return meta; // FIXME: this does nothing, we should parse here, or at the document level to validate meta for each type
  }

  protected createthirdPartyAuth(
    thirdPartyAuth: ThirdPartyAuthDetails
  ): ThirdPartyAuthDetails {
    if (thirdPartyAuth) {
      return new ThirdPartyAuthModel(thirdPartyAuth);
    }
    return null;
  }

  protected createGenerator(generatorConfig: Generator): Generator {
    if (generatorConfig) {
      return new GeneratorModel(generatorConfig);
    }
    return null;
  }

  protected createContact(contactConfig: Contact) {
    if (contactConfig) {
      return new ContactModel(contactConfig);
    }
    return null;
  }

  protected createDisposalSite(disposalSiteConfig: DisposalSite) {
    if (disposalSiteConfig) {
      return new DisposalSiteModel(disposalSiteConfig);
    }
    return null;
  }

  protected createSignatures(signaturesConfig: Signature[]): Signature[] {
    if (!signaturesConfig) {
      return [];
    }
    return signaturesConfig.map(
      (signatureConfig) => new SignatureModel(signatureConfig)
    );
  }

  protected createSignNowDocument(signNowDocumentConfig: SignNowDocument) {
    if (signNowDocumentConfig) {
      return new SignNowDocumentModel(signNowDocumentConfig);
    }
    return null;
  }

  protected createCoordinator(coordinatorConfig: User) {
    if (coordinatorConfig) {
      return new UserModel(coordinatorConfig);
    }
    return null;
  }

  protected createPendingRecertification(
    recertificationConfig: RecertificationDetails
  ): RecertificationDetails {
    if (recertificationConfig) {
      return new RecertificationDetailsModel(recertificationConfig);
    }

    return null;
  }

  protected createRecertificationArchive(
    recertificationArchiveConfig: RecertificationDetails[]
  ): RecertificationDetails[] {
    if (recertificationArchiveConfig) {
      return recertificationArchiveConfig.map(
        (config) => new RecertificationDetailsModel(config)
      );
    }
    return [];
  }

  protected createPendingChangeRequest(
    changeRequestConfig: ChangeRequestDetails
  ): ChangeRequestDetails {
    if (changeRequestConfig) {
      return new ChangeRequestDetailsModel(changeRequestConfig);
    }

    return null;
  }

  protected createChangeRequestArchive(
    changeRequestArchiveConfig: ChangeRequestDetails[]
  ): ChangeRequestDetails[] {
    if (changeRequestArchiveConfig) {
      return changeRequestArchiveConfig.map(
        (config) => new ChangeRequestDetailsModel(config)
      );
    }
    return [];
  }

  protected createCheckList(checkListConfig: CheckList): CheckList {
    if (checkListConfig) {
      return new CheckListModel(checkListConfig);
    }

    return null;
  }

  protected createLog(
    logConfig: InternalStatusHistory[]
  ): InternalStatusHistory[] {
    if (!Array.isArray(logConfig)) {
      return [];
    }

    return logConfig.map((logEntry) =>
      InternalStatusHistoryModel.hydrate(logEntry)
    );
  }

  protected createCommentLog(
    commentLogConfig: CommentLogHistory[]
  ): CommentLogHistory[] {
    if (!Array.isArray(commentLogConfig)) {
      return [];
    }

    return commentLogConfig.map(
      (commentLogEntry) => new CommentLogHistoryModel(commentLogEntry)
    );
  }
}
